import { Link } from 'react-router-dom';
import { CREDIT_COPY, DEFAULT_DISCLAIMER, UNPUBLISHED_RANGE, formatRange, formatUsd, type usePublicPricing } from '@/lib/pricing';

interface Props {
  pricing: ReturnType<typeof usePublicPricing>;
  slug: string;
  serviceName: string;
}

// Step 4 informational summary. No payment, no agreed price: submitting is not price approval.
export default function PricingSummary({ pricing, slug, serviceName }: Props) {
  const { state, data, priceFor, reload } = pricing;
  const p = state === 'ready' ? priceFor(slug) : null;
  const fee = state === 'ready' ? data?.diagnostic_fee : null;
  const rows: [string, string][] = [['Service', serviceName]];
  if (state === 'loading') rows.push(['Typical cost', 'Loading…']);
  else if (state === 'error') rows.push(['Typical cost', 'Couldn’t load pricing']);
  else rows.push(['Typical cost', p ? `${formatRange(p)} (indicative)` : UNPUBLISHED_RANGE]);
  if (fee) {
    rows.push(['Diagnostic visit', fee.amount_cents > 0 ? formatUsd(fee.amount_cents) : 'No fee']);
    if (fee.amount_cents > 0) rows.push(['Fee credit', fee.credited_toward_repair ? CREDIT_COPY : 'Not credited toward the repair.']);
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5 text-sm">
      <p className="text-xs font-medium uppercase tracking-[0.1em] text-foreground-700">Pricing information</p>
      <dl className="mt-3">
        {rows.map(([k, val]) => (
          <div key={k} className="grid grid-cols-[120px_1fr] gap-3 border-b border-background-200 py-2 last:border-0">
            <dt className="text-foreground-600">{k}</dt>
            <dd className="min-w-0 break-words text-foreground-950">{val}</dd>
          </div>
        ))}
      </dl>
      {fee?.explanation && <p className="mt-3 text-xs leading-relaxed text-foreground-600">{fee.explanation}</p>}
      <p className="mt-3 text-xs leading-relaxed text-foreground-600">
        {(data?.disclaimer || DEFAULT_DISCLAIMER)} Submitting this request does not approve a repair price, and no payment is taken.{' '}
        {state === 'error'
          ? <button type="button" onClick={reload} className="cursor-pointer whitespace-nowrap underline">Retry</button>
          : <Link to="/pricing" target="_blank" className="whitespace-nowrap text-primary-700 underline-offset-2 hover:underline">How pricing works</Link>}
      </p>
    </div>
  );
}